import { useContext } from "react";

import { getRanaApiV1 } from "@nens/rana/api-v1";
import { useMutationWithErrorHandler } from "@nens/ranalib";

import { useInvalidateFilesAndHistoryQueries } from "@/queries/refetch-files-and-history";
import { ProjectContext } from "@/stores/project-provider";
import { getParentPath } from "@/utils/get-file-parent-path";

import { FileOrDirectoryEnum } from "../types/file-or-directory";

type DuplicateArgs = {
  projectId: string;
  sourceId: string;
  type: FileOrDirectoryEnum;
  destinationId: string;
};

const duplicateFileOrDirectory = async ({
  projectId,
  sourceId,
  destinationId,
  type,
}: DuplicateArgs) => {
  return await getRanaApiV1().FilesApi[
    type === "file" ? "projectFileCopy" : "projectDirectoryCopy"
  ]({ sourcePath: sourceId, destinationPath: destinationId, projectId });
};

export const useDuplicateProjectFileOrDirectory = () => {
  const { id: projectId } = useContext(ProjectContext);
  const invalidateFilesAndHistoryQueries = useInvalidateFilesAndHistoryQueries();

  return useMutationWithErrorHandler(
    async ({ sourceId, destinationId, type }: Omit<DuplicateArgs, "projectId">) => {
      const response = await duplicateFileOrDirectory({
        projectId,
        sourceId,
        destinationId,
        type,
      });
      invalidateFilesAndHistoryQueries(projectId, response.id);
    }
  );
};

export const useDuplicateProjectFilesOrDirectories = () => {
  const { id: projectId } = useContext(ProjectContext);
  const invalidateFilesAndHistoryQueries = useInvalidateFilesAndHistoryQueries();

  return useMutationWithErrorHandler(async (items: Omit<DuplicateArgs, "projectId">[]) => {
    const promises = items.map(({ sourceId, destinationId, type }) =>
      duplicateFileOrDirectory({
        projectId,
        sourceId,
        destinationId,
        type,
      })
    );
    const responses = await Promise.all(promises);

    const invalidatedParentPaths = new Set<string>();

    responses.forEach((response) => {
      const parentPath = getParentPath(response.id);
      if (!invalidatedParentPaths.has(parentPath)) {
        invalidatedParentPaths.add(parentPath);
        invalidateFilesAndHistoryQueries(projectId, response.id);
      }
    });
  });
};
